<template>
    <section
        :id="slice.primary.anchorName || undefined"
        :data-mui-theme="theme"
        class="slice slice-form slice-form--with-shapes"
        aria-label="form"
        data-testid="slice-form-with-shapes"
    >
        <div class="slice-content slice-form--with-shapes__content mui-background-lighter">
            <template v-if="isFilled.image(slice.primary.image)">
                <svg viewBox="0 0 1108 928" class="slice-form--with-shapes__shapes">
                    <path :d="shapePaths.s" fill="var(--joy-color-neutral-0)" class="slice-form--with-shapes__shape-s" />
                    <path :d="shapePaths.m" class="slice-form--with-shapes__shape-m" />

                    <mask id="mask0_692_4413" style="mask-type: alpha" maskUnits="userSpaceOnUse" x="-124" y="81" width="1060" height="2399">
                        <path :d="shapePaths.l" class="slice-form--with-shapes__shape-l" />
                    </mask>
                    <g :mask="`url(#mask0_692_4413)`">
                        <rect width="1002.21" height="880.75" transform="matrix(-1 0 0 1 699.334 45.9722)" :fill="`url(#pattern0_692_4413)`" />
                    </g>
                    <defs>
                        <pattern id="pattern0_692_4413" patternContentUnits="objectBoundingBox" width="1" height="1">
                            <use href="#image0_692_4413" transform="matrix(0.000321788 0 0 0.000366166 -0.159022 0)" />
                        </pattern>
                        <image id="image0_692_4413" width="4096" height="2731" :href="imageUrl ?? undefined" preserveAspectRatio="none" />
                    </defs>
                </svg>
            </template>
            <div
                class="slice-form--with-shapes__card-wrapper"
                :class="{'slice-form--with-shapes__card-wrapper--ready': formReady}"
                data-testid="slice-form-with-shapes-card-wrapper"
            >
                <div class="slice-form--with-shapes__card">
                    <PrismicRichText :field="slice.primary.title" data-testid="slice-form-title" />
                    <div data-testid="slice-form-with-shapes-hubspot">
                        <MktHubspotForm
                            class="slice-form--form-with-shapes__hubspot-form"
                            :portal-id="slice.primary.hubspotPortalId + ''"
                            :form-id="slice.primary.hubspotFormId + ''"
                            :region="slice.primary.hubspotRegion + ''"
                            :salesforce-id="slice.primary.salesforceId + ''"
                            @submit="onFormSubmit"
                            @ready="onFormReady"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, inject, ref} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {FormSlice} from '@prismicio-types';
    import {MktHubspotForm} from '#components';
    import {useDataTracking} from '#imports';

    type FormSliceWithShapesInZone = Extract<FormSlice, {variation: 'formWithShapes'}>;

    const props = defineProps(getSliceComponentProps<FormSliceWithShapesInZone>());
    const {slice} = props;

    const {track, extractWebAttributionProperties} = useDataTracking();

    const pageCategory = inject('pageCategory');

    const formElement = ref<HTMLFormElement>();
    const formReady = ref(false);

    const imageUrl = computed(() => (isFilled.image(slice.primary.image) ? slice.primary.image.url : null));

    const shapePaths = {
        l: 'M101.484 95.3631C361.584 25.3198 456.329 231.849 499.753 401.005L909.989 1999.07C953.366 2168.24 970.053 2395.79 709.999 2465.82C449.899 2535.87 355.154 2329.34 311.73 2160.18L-98.506 562.112C-141.93 392.956 -158.616 165.406 101.484 95.3631Z',
        s: 'M298.615 267.433C386.878 125.994 512.666 178.41 600.379 235.803L1429.04 778.008C1516.74 835.426 1616.04 930.288 1527.79 1071.7C1439.52 1213.14 1313.74 1160.72 1226.02 1103.33L397.365 561.126C309.651 503.734 210.352 408.872 298.615 267.433Z',
        m: 'M106.924 39.3346C271.146 -64.1454 378.118 57.3538 442.284 164.15L1048.48 1173.08C1112.62 1279.9 1170.31 1432.45 1006.12 1535.91C841.895 1639.39 734.923 1517.89 670.757 1411.1L64.5572 402.165C0.390828 295.369 -57.2972 142.815 106.924 39.3346Z',
    };

    const theme = computed(() => slice.primary.theme);

    function onFormReady(form: HTMLFormElement) {
        formReady.value = true;
        formElement.value = form;
    }

    function onFormSubmit(form: HTMLFormElement) {
        const h2 = document.querySelector('h2');
        const typeField = form.querySelector(`.hs-input[name=form_type]`) as HTMLInputElement;
        const eventProperties = {
            url: window.location.pathname,
            page_title_h1: h2?.innerText,
            page_category: pageCategory,
            form_type: typeField?.value || null,
            form_name: form.dataset.formId,
        };

        const userProperties = {
            ...extractWebAttributionProperties(),
            referrer: document.referrer || null,
        };

        track('website_form_submitted', eventProperties, userProperties);
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-form--form-with-shapes__hubspot-form {
        label span:not(ul > li > label > span) {
            color: var(--joy-color-neutral-60);
            line-height: var(--joy-line-height-large);
            font-size: var(--joy-font-size-primary-600);
            font-weight: var(--joy-font-weight-bold);
        }

        .hs-form-field input.invalid.error,
        .hs-form-field select.invalid.error {
            border-color: var(--joy-color-primary-50);
        }

        .hs_error_rollup label {
            color: var(--joy-color-primary-50);
        }

        .hs-form-field label {
            display: none;
        }

        .hs_submit input[type='submit'] {
            border: 1px solid var(--joy-color-primary-50);
            background-color: var(--joy-color-primary-50);

            &:hover {
                background-color: var(--joy-color-primary-70);
                border: 1px solid var(--joy-color-primary-70);
            }
        }

        a {
            color: var(--joy-color-neutral-50);
            text-decoration: underline;
            text-underline-offset: 3px;
        }

        ul[role='checkbox'] {
            display: flex;
            flex-wrap: wrap;
            gap: var(--joy-core-spacing-4);
        }

        .actions {
            float: left;
        }
    }

    .slice-form--with-shapes {
        @include mq.screen_md(min) {
            padding: 0 var(--joy-core-spacing-6);
        }

        &__content {
            padding: var(--joy-core-spacing-4);
            position: relative;
            display: grid;
            gap: var(--joy-core-spacing-8);
            width: 100%;
            border-radius: var(--joy-core-spacing-6);

            @include mq.screen_sm(min) {
                padding: 80px var(--joy-core-spacing-6);
            }

            @include mq.screen_lg(min) {
                grid-template-columns: minmax(550px, 1fr) minmax(0, 650px);
                gap: var(--joy-core-spacing-2);
                padding: 100px var(--joy-core-spacing-6);
            }
        }

        &__shapes {
            position: absolute;
            bottom: -1px;
            left: 0;
            height: 100%;
            border-radius: var(--joy-core-spacing-7);
        }

        &__shape-l {
            display: none;
            @include mq.screen_lg(min) {
                display: block;
            }
        }

        &__shape-m {
            translate: -490px 490px;
            fill: var(--mui-color-shape);
            @include mq.screen_lg(min) {
                translate: 280px 490px;
            }
        }

        &__shape-s {
            translate: -620px 410px;
            @include mq.screen_lg(min) {
                translate: 300px 600px;
            }
        }

        &__card-wrapper {
            display: flex;
            justify-content: center;
            transition: opacity var(--joy-transition-duration-default) var(--joy-transition-timing-function);
            opacity: 0;

            @include mq.screen_lg(min) {
                grid-column: 2;
                justify-content: flex-start;
            }

            &--ready {
                opacity: 1;
            }
        }

        &__card {
            z-index: 1;
            background-color: var(--joy-color-neutral-0);
            padding: var(--joy-core-spacing-6);
            box-shadow: var(--joy-core-elevation-0);
            border-radius: var(--joy-core-radius-4);

            h2 {
                font-size: var(--joy-font-size-secondary-400);
                font-family: var(--joy-font-family-title);
                line-height: var(--joy-line-height-small);
                margin-bottom: var(--joy-core-spacing-6);
            }

            @include mq.screen_md(min) {
                padding: var(--joy-core-spacing-8);
                min-width: 0;
                max-width: 610px;
            }
        }
    }
</style>
